import React, { useEffect, useRef, useState } from 'react';

/**
 * SeanceWhispers — the living player's view of ghost chat (Nina 07-30).
 *
 * NOT a new channel and NOT a new mechanic: this is a presentation layer over the
 * ghost messages that ALREADY flow to every client via `spectatingMessages`. The
 * dead keep typing into the same ghost chat they always had; living players now
 * see those lines drift across the scene for a few seconds and fade, instead of
 * only ever meeting them inside a collapsed panel.
 *
 * Why a layer and not a band: the whole mobile problem was that ghost chat held a
 * permanent ~150px on a ~470px screen. An absolutely-positioned overlay costs ZERO
 * permanent pixels, so the room can feel populated without charging rent for it.
 *
 * MOBILE ONLY (Nina 07-30). Desktop has the room for the real ghost panel and keeps
 * it — whispers exist to buy back vertical space that only phones are short of, so
 * on desktop they would be pure noise over a panel that already shows the same text.
 * The caller gates the mount on `isMobile`; both bottom offsets below are phone
 * figures and would be wrong on desktop chrome.
 *
 * Ghosts do NOT know who the killer is (Nina 07-30 — deliberately, so whispers stay
 * opinions rather than spoilers). That decision is what makes this safe to float over
 * a live scene at all; revisit this component if ghost sight is ever added.
 *
 * Rules this obeys:
 *   · pointer-events: none — a whisper can NEVER eat a tap on a clue or a button.
 *     Only the mute affordance opts back in.
 *   · zIndex 4700: above the investigation takeover (1200) AND the places
 *     layers (4600 — whispers ride every place, Nina 08-02), below the ghost
 *     edge tab (4800) / dock (5000) / nav (5100), far below cinematics (300000).
 *   · At most MAX_LIVE on screen, oldest evicted — a busy gallery must not turn the
 *     scene into soup.
 *   · Only messages that arrive AFTER mount whisper. History stays in the panel,
 *     otherwise opening a game would fire a backlog at you.
 *   · prefers-reduced-motion: no drift, no fade-in. The line simply appears and
 *     leaves. Nothing is conveyed by motion alone.
 *   · The ghost chat panel/sheet remains the canonical, scrollable, screen-reader
 *     log. This layer is aria-hidden; it is decoration over a real channel, never
 *     the only way to read a message.
 */

const MAX_LIVE = 3;          // concurrent whispers on screen
const LIFETIME_MS = 7000;    // how long one whisper lives
const MIN_GAP_MS = 900;      // floor between two whispers surfacing
const MUTE_KEY = 'seanceWhispersMuted';

// 🔝 Top-LEFT band experiment (Nina 08-28): whispers drift DOWN from the
// header zone instead of hovering over the newest-message zone + dock — the
// bottom band sat on the most-read pixels of the chat. Left side per Nina
// (the LofiRadio FAB owns top-right); the top offset clears the Back button
// and the notch, and the band still yields z to toasts/poll bar. Flip to
// false to restore the 07-30 bottom band — the measured-dock machinery below
// is kept intact for exactly that revert.
const TOP_LEFT_BAND = true;

// Cold blue-white against the noir's warm brass — the temperature difference is
// what reads as "not of this room" without needing a second accent hue.
const GHOST_INK = '#CFE0E8';
const GHOST_DIM = '#7FA6B8';

const readMuted = () => {
    try { return window.localStorage.getItem(MUTE_KEY) === '1'; } catch (e) { return false; }
};

const SeanceWhispers = ({
    messages = [],
    active = false,
    overStage = false,
    // The composer dock's ref (GameStepPage inputFormRef). When present the
    // band anchors to the dock's MEASURED top instead of the static offset —
    // see the bottom calc below for why.
    anchorRef = null,
    // Mute-button copy: noir/comp keeps the séance flavor, regular spaces
    // get the plain version (Nina 08-02)
    competitive = false,
}) => {
    const [live, setLive] = useState([]);
    const [muted, setMuted] = useState(readMuted);
    // Start from the CURRENT length: everything already on screen is history.
    const seenRef = useRef(null);
    const queueRef = useRef([]);
    const lastAtRef = useRef(0);
    const timersRef = useRef([]);

    // 📏 Measured dock top (Nina 08-14): the static 150px offset only cleared
    // the dock FOLDED (53px nav + one-line handle). An expanded composer, or a
    // poll/picker riding the dock, grows past ~97px and buries the band —
    // dock z5000 beats band z4700 on purpose (whispers must never cover
    // controls), so the band has to move, not win. Same lesson as the feed's
    // bottom reserve (GameStepPage 08-02): a fixed figure lies whenever the
    // dock grows. Measured on RO events + when a whisper surfaces — never on
    // a steady interval (no per-tick forced layout while the band is idle).
    const [dockTop, setDockTop] = useState(null);
    const measureRef = useRef(() => {});
    measureRef.current = () => {
        const el = anchorRef && anchorRef.current;
        if (!el) { setDockTop(null); return; }
        const r = el.getBoundingClientRect();
        // height 0 = dock unmounted or mid-swap (place hop, status flip):
        // fall back to the static offset rather than anchor to a dead rect.
        setDockTop(r.height > 0 ? Math.round(r.top) : null);
    };
    useEffect(() => {
        // Top band doesn't anchor to the dock — skip the observer entirely
        // (machinery kept for the TOP_LEFT_BAND=false revert).
        if (TOP_LEFT_BAND) return undefined;
        if (!active || muted) return undefined;
        const el = anchorRef && anchorRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver(() => measureRef.current());
        ro.observe(el);
        measureRef.current();
        return () => ro.disconnect();
    }, [active, muted, anchorRef]);

    // Drain the queue on a floor-spaced interval so a burst of five ghost lines
    // arriving in one poll surfaces as a readable trickle, not a pile.
    useEffect(() => {
        if (!active || muted) return undefined;
        const iv = setInterval(() => {
            if (!queueRef.current.length) return;
            if (Date.now() - lastAtRef.current < MIN_GAP_MS) return;
            const next = queueRef.current.shift();
            lastAtRef.current = Date.now();
            // Re-measure as each whisper surfaces: the RO can be holding a
            // node the last place-hop unmounted, and surfacing is rare enough
            // that one getBoundingClientRect here is free.
            measureRef.current();
            setLive(cur => [...cur, next].slice(-MAX_LIVE));
            const t = setTimeout(() => {
                setLive(cur => cur.filter(w => w.key !== next.key));
            }, LIFETIME_MS);
            timersRef.current.push(t);
        }, 300);
        return () => clearInterval(iv);
    }, [active, muted]);

    useEffect(() => () => { timersRef.current.forEach(clearTimeout); }, []);

    useEffect(() => {
        const list = Array.isArray(messages) ? messages : [];
        // First run (or a game switch that shortened the list): treat everything
        // present as already seen and whisper nothing.
        if (seenRef.current === null || list.length < seenRef.current) {
            seenRef.current = list.length;
            return;
        }
        if (list.length === seenRef.current) return;
        const fresh = list.slice(seenRef.current);
        seenRef.current = list.length;
        if (!active || muted) return;
        fresh.forEach((m, i) => {
            // Only human gallery chatter whispers. Bulletins, the moderator and
            // system notes have their own surfaces and would be noise here.
            if (!m || m.is_announcement || m.is_moderator || m.is_reaction_note) return;
            if (m.sender_id === 'system') return;
            const text = String(m.message || '').trim();
            if (!text) return;
            queueRef.current.push({
                key: `${m.id || 'w'}-${seenRef.current}-${i}`,
                sender: m.sender || 'someone',
                text: text.length > 140 ? `${text.slice(0, 139)}…` : text,
            });
        });
    }, [messages, active, muted]);

    useEffect(() => {
        if (!active) { setLive([]); queueRef.current = []; }
    }, [active]);

    if (!active || muted || !live.length) return null;

    // Bottom-anchored above whatever chrome owns the bottom edge in this state:
    // the stage's manila desk band, or the ghost/input dock sitting over the nav.
    // All offsets are phone figures — the caller mounts this on mobile only.
    const bottom = overStage
        // 132: the manila desk band runs ~122px tall when a pack's wide font
        // wraps the hint to two lines (probe 07-31: the mute pill at 104
        // grazed the band's top text). The takeover unmounts the dock on
        // mobile, so there is nothing to measure in this state.
        ? 'calc(132px + env(safe-area-inset-bottom, 0px))'
        : (dockTop !== null
            // Measured path: dock top already includes nav + safe-area, so no
            // env() term here. +8 breathing room over the dock's border.
            ? `${Math.max(0, Math.round(window.innerHeight - dockTop) + 8)}px`
            // Static fallback (no anchorRef / dock unmounted / no RO): the
            // pre-08-14 figure, clears the folded dock.
            : 'calc(150px + env(safe-area-inset-bottom, 0px))');

    return (
        <div
            aria-hidden="true"
            style={{
                // z4700 (was 1250, Nina 08-02): the places layers sit at 4600
                // and were burying the whispers on TV/Locals/Arcade — the
                // séance rides EVERY place. Still under the edge tab (4800),
                // dock (5000), and nav (5100).
                position: 'fixed', left: 0, right: 0, zIndex: 4700,
                // Top-left (Nina 08-28): 52px clears the Back button's row at
                // scroll-top AND sits below the LofiRadio FAB band (top 10 +
                // 34px). Bottom mode: the measured/static dock offsets.
                ...(TOP_LEFT_BAND
                    ? { top: 'calc(env(safe-area-inset-top, 0px) + 52px)' }
                    : { bottom }),
                pointerEvents: 'none',
                // Bottom mode was right-docked (Nina 08-03: left edge = where
                // message bubbles live, so left-hugging whispers read as chat).
                // Top mode is LEFT (Nina 08-28): up here the left edge is
                // masthead/header zone, not bubbles, and top-right is the
                // FAB's corner.
                display: 'flex', flexDirection: 'column',
                alignItems: TOP_LEFT_BAND ? 'flex-start' : 'flex-end',
                gap: 6, padding: '0 12px',
            }}
        >
            <style>{`
                @keyframes seanceDrift {
                    0%   { opacity: 0; transform: translateY(10px); }
                    14%  { opacity: 1; transform: translateY(0); }
                    72%  { opacity: 1; transform: translateY(-4px); }
                    100% { opacity: 0; transform: translateY(-14px); }
                }
                @keyframes seanceDriftDown {
                    0%   { opacity: 0; transform: translateY(-10px); }
                    14%  { opacity: 1; transform: translateY(0); }
                    72%  { opacity: 1; transform: translateY(4px); }
                    100% { opacity: 0; transform: translateY(14px); }
                }
                .seance-w { animation: ${TOP_LEFT_BAND ? 'seanceDriftDown' : 'seanceDrift'} ${LIFETIME_MS}ms ease-in-out forwards; }
                @media (prefers-reduced-motion: reduce) {
                    .seance-w { animation: none; opacity: 1; transform: none; }
                }
            `}</style>

            {live.map(w => (
                <div
                    key={w.key}
                    className="seance-w"
                    style={{
                        maxWidth: '84%',
                        // The scrim is not optional: these float over AI-generated
                        // scene art, so legibility can't depend on which pixel
                        // happens to land underneath (same fix as hotspot labels).
                        background: 'rgba(8,10,14,0.52)',
                        backdropFilter: 'blur(3px)',
                        WebkitBackdropFilter: 'blur(3px)',
                        borderRight: `2px solid ${GHOST_DIM}88`,
                        borderRadius: '8px 0 0 8px',
                        padding: '5px 9px',
                        textShadow: '0 1px 3px rgba(0,0,0,0.95)',
                    }}
                >
                    <div style={{
                        fontSize: '0.6em', fontWeight: 700, letterSpacing: '0.1em',
                        textTransform: 'uppercase', color: GHOST_DIM, marginBottom: 2,
                    }}>
                        👻 {w.sender}
                    </div>
                    <div style={{
                        fontSize: '0.82em', fontStyle: 'italic', lineHeight: 1.35,
                        color: GHOST_INK,
                    }}>
                        {w.text}
                    </div>
                </div>
            ))}

            <button
                onClick={() => {
                    setMuted(true);
                    setLive([]);
                    queueRef.current = [];
                    try { window.localStorage.setItem(MUTE_KEY, '1'); } catch (e) { /* private mode */ }
                }}
                aria-label="Mute ghost whispers"
                style={{
                    pointerEvents: 'auto', alignSelf: 'flex-end',
                    background: 'rgba(8,10,14,0.6)', color: GHOST_DIM,
                    border: `1px solid ${GHOST_DIM}55`, borderRadius: 999,
                    padding: '3px 9px', fontSize: '0.6em', fontWeight: 700,
                    letterSpacing: '0.08em', cursor: 'pointer', fontFamily: 'inherit',
                    minHeight: 24,
                }}
            >
                {competitive ? '🔇 QUIET THE DEAD' : '🔇 SILENCE GHOSTS'}
            </button>
        </div>
    );
};

export default SeanceWhispers;
